import { useEffect, useState } from "react";
import { platformMap } from "../data/platforms";
import { api } from "../lib/api";
import { formatRelativeTime } from "../lib/format";
import type { SourceHealth } from "../types";
import { Icon } from "./Icon";

interface AdminPanelProps {
  open: boolean;
  onClose: () => void;
}

export function AdminPanel({ open, onClose }: AdminPanelProps) {
  const [sources, setSources] = useState<SourceHealth[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshingId, setRefreshingId] = useState<string | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError("");
    api.getSourceHealth()
      .then(setSources)
      .catch((reason) => setError(reason instanceof Error ? reason.message : "无法读取数据源状态"))
      .finally(() => setLoading(false));
  }, [open]);

  async function refresh(platformId: string) {
    setRefreshingId(platformId);
    setError("");
    try {
      await api.refreshSource(platformId);
      setSources(await api.getSourceHealth());
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "刷新失败");
    } finally {
      setRefreshingId(null);
    }
  }

  if (!open) return null;

  return (
    <div className="modal-layer">
      <button className="modal-scrim" aria-label="关闭数据源管理" onClick={onClose} type="button" />
      <section className="admin-modal" aria-modal="true" role="dialog" aria-labelledby="admin-title">
        <header className="modal-heading">
          <div><span className="section-index">管理</span><h2 id="admin-title">数据源状态</h2></div>
          <button className="icon-button" aria-label="关闭" onClick={onClose} type="button"><Icon name="close" /></button>
        </header>
        {error ? <p className="form-message is-error admin-error">{error}</p> : null}
        {loading ? <div className="admin-loading">正在读取数据源状态…</div> : (
          <div className="source-table" role="table">
            <div className="source-row source-header" role="row">
              <span>平台</span><span>状态</span><span>最后成功</span><span>连续失败</span><span>操作</span>
            </div>
            {sources.map((source) => (
              <div className="source-row" role="row" key={source.platformId} title={source.error || undefined}>
                <strong>{platformMap.get(source.platformId)?.name || source.platformId}</strong>
                <span className={`source-status status-${source.status}`}>{source.status === "healthy" ? "正常" : source.status === "stale" ? "缓存" : source.status === "collecting" ? "采集中" : "不可用"}</span>
                <span>{formatRelativeTime(source.lastSuccessAt)}</span>
                <span>{source.consecutiveFailures}</span>
                <button disabled={refreshingId === source.platformId} onClick={() => refresh(source.platformId)} type="button">
                  <Icon name="refresh" />{refreshingId === source.platformId ? "刷新中" : "刷新"}
                </button>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
